// ATTP Corporate Hub — Business Consulting · Corporate Training · Professional Education.
// Public-facing site with main landing and three dedicated pillar pages.
import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import {
  ChevronRight, ArrowRight, CheckCircle2, Briefcase, Users, GraduationCap,
  Mail, Phone, MapPin, Sparkles, Award, Building, Globe2,
} from 'lucide-react';
import PublicSiteShell from '@/components/layout/PublicSiteShell';
import { CORPORATE_BRAND, CORPORATE_PILLARS, ABOUT_CONTENT } from '@/lib/publicSiteContent';

const ICON_BY_PILLAR = {
  'business-consulting': Briefcase,
  'corporate-training': Users,
  'professional-education': GraduationCap,
};

const CORPORATE_MENU = [
  { label: 'Home', href: '/corporate' },
  {
    label: 'Services',
    dropdown: CORPORATE_PILLARS.map((p) => ({ label: p.title, href: `/corporate/${p.slug}` })),
  },
  {
    label: 'About Us',
    dropdown: [
      { label: 'About ATTP Corporate Hub', href: '/about/about-attp' },
      { label: 'Mission & Vision', href: '/about/mission-vision' },
      { label: 'Core Values', href: '/about/core-values' },
      { label: 'Lead Consultant & Founder', href: '/about/lead-consultant' },
      { label: 'Countries of Operation', href: '/about/countries-of-operation' },
      { label: 'Why Choose Us', href: '/about/why-choose-us' },
      { label: 'Our Approach', href: '/about/our-approach' },
      { label: 'Contact Us', href: '/about/contact-us' },
    ],
  },
  { label: 'Engineering', href: '/engineering' },
  { label: 'Contact', href: '/about/contact-us' },
];

export { CORPORATE_MENU };

/* ================================================================== */
/*  CORPORATE HUB — MAIN LANDING                                       */
/* ================================================================== */
export default function CorporateMainPage() {
  const b = CORPORATE_BRAND;
  return (
    <PublicSiteShell brand={b} menu={CORPORATE_MENU} chatContext="Visitor on ATTP Corporate Hub site.">
      {/* HERO */}
      <section id="home" className="relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${b.primary} 0%, #2a2620 100%)`, color: '#FFFFFF' }} data-testid="corporate-hero">
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full blur-[120px]" style={{ background: `${b.accent}30` }} />
        <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] rounded-full blur-[120px]" style={{ background: `${b.secondary}25` }} />
        <div className="container-x relative pt-20 pb-24 lg:pt-28 lg:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.18em] mb-6" style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}>
              <span className="h-1.5 w-1.5 rounded-full animate-pulse" style={{ background: b.accent }} />
              Active across 13+ African countries
            </div>
            <h1 className="font-display font-bold tracking-tight text-4xl sm:text-5xl lg:text-[58px] leading-[1.05] text-balance">
              Building Stronger <span style={{ color: b.accent }}>African Institutions.</span>
            </h1>
            <p className="mt-6 text-lg leading-relaxed max-w-2xl" style={{ color: 'rgba(255,255,255,0.78)' }}>
              ATTP Corporate Hub is a professional consulting, training and education practice — dedicated to helping African institutions build structured systems, develop capable leaders and achieve sustainable, intergenerational growth.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#pillars" className="px-6 py-3 rounded-md font-semibold text-sm inline-flex items-center gap-2 transition-transform hover:scale-[1.03]" style={{ background: b.accent, color: b.primary }} data-testid="corporate-cta-services">
                View Our Services <ChevronRight className="w-4 h-4" />
              </a>
              <Link to="/about/contact-us" className="px-6 py-3 rounded-md font-semibold text-sm border border-white/25 text-white hover:bg-white/10 transition-colors">
                Request a Consultation
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative aspect-[4/5] rounded-md overflow-hidden border shadow-2xl" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
              <img src="https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=1200&q=80" alt="Corporate consulting" className="w-full h-full object-cover" />
              <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${b.primary}, transparent)` }} />
            </div>
          </div>
        </div>

        {/* stats */}
        <div className="relative border-t border-white/10 bg-black/30 py-5">
          <div className="container-x grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="font-display font-bold text-2xl lg:text-3xl" style={{ color: b.accent }}>13+</div>
              <div className="text-[11px] uppercase tracking-[0.18em]" style={{ color: 'rgba(255,255,255,0.55)' }}>African countries</div>
            </div>
            <div>
              <div className="font-display font-bold text-2xl lg:text-3xl" style={{ color: b.accent }}>100+</div>
              <div className="text-[11px] uppercase tracking-[0.18em]" style={{ color: 'rgba(255,255,255,0.55)' }}>Institutions served</div>
            </div>
            <div>
              <div className="font-display font-bold text-2xl lg:text-3xl" style={{ color: b.accent }}>20+</div>
              <div className="text-[11px] uppercase tracking-[0.18em]" style={{ color: 'rgba(255,255,255,0.55)' }}>Years of practice</div>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="py-20 bg-white">
        <div className="container-x">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.22em] font-semibold mb-3" style={{ color: b.accent }}>About</div>
            <h2 className="font-display font-bold text-4xl tracking-tight leading-tight">
              A practice built for African institutions.
            </h2>
            <p className="mt-5 text-lg text-neutral-600 leading-relaxed">
              ATTP Corporate Hub is a professional consulting, training and education practice dedicated to helping organisations build structured systems, develop leadership capacity and achieve sustainable institutional growth. Our approach is practical, grounded in real institutional experience and deeply informed by African contextual realities.
            </p>
            <Link to="/about/about-attp" className="mt-6 inline-flex items-center gap-2 font-semibold text-sm" style={{ color: b.accent }}>
              Read the full About page <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* THREE PILLARS — side by side */}
      <section id="pillars" className="py-20 lg:py-24" style={{ background: '#FAF8F2' }}>
        <div className="container-x">
          <div className="max-w-3xl mb-12">
            <div className="text-xs uppercase tracking-[0.22em] font-semibold mb-3" style={{ color: b.accent }}>Three pillars. One transformation practice.</div>
            <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight leading-[1.05] text-balance">
              Consulting. Training. Education.
            </h2>
            <p className="mt-5 text-lg text-neutral-600 leading-relaxed">
              Our practice operates through three integrated service pillars — each delivered by specialist senior consultants with deep institutional experience and Pan-African delivery capability.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {CORPORATE_PILLARS.map((p) => {
              const Icon = ICON_BY_PILLAR[p.slug] || Briefcase;
              return (
                <Link
                  key={p.slug}
                  to={`/corporate/${p.slug}`}
                  className="group p-7 bg-white rounded-md border transition-all hover:-translate-y-1 hover:shadow-2xl"
                  style={{ borderColor: '#E5E7EB' }}
                  data-testid={`corporate-pillar-${p.slug}`}
                >
                  <div className="flex items-start justify-between mb-5">
                    <div className="h-14 w-14 rounded-md flex items-center justify-center" style={{ background: `${b.accent}15`, color: b.accent }}>
                      <Icon className="w-7 h-7" strokeWidth={1.5} />
                    </div>
                    <ArrowRight className="w-5 h-5 text-neutral-300 group-hover:translate-x-1 transition-all" />
                  </div>
                  <div className="text-[10px] uppercase tracking-wider font-semibold mb-2" style={{ color: b.accent }}>Service pillar</div>
                  <h3 className="font-display font-semibold text-2xl mb-3 leading-tight" style={{ color: b.primary }}>{p.title}</h3>
                  <p className="text-sm text-neutral-600 leading-relaxed mb-4">{p.short}</p>
                  <div className="text-xs text-neutral-500 pt-3 border-t" style={{ borderColor: '#F1F1F1' }}>
                    {p.items.length} focus areas in this pillar
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="py-20 bg-white">
        <div className="container-x">
          <div className="max-w-3xl mb-10">
            <div className="text-xs uppercase tracking-[0.22em] font-semibold mb-3" style={{ color: b.accent }}>Why work with us</div>
            <h2 className="font-display font-bold text-3xl lg:text-4xl tracking-tight leading-tight">
              We do not just deliver services. We drive transformation.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {ABOUT_CONTENT['why-choose-us'].points.map((p) => (
              <div key={p.name} className="p-5 rounded-md border" style={{ borderColor: '#E5E7EB' }}>
                <div className="flex items-center gap-2 mb-1.5">
                  <CheckCircle2 className="w-5 h-5" style={{ color: b.accent }} />
                  <div className="font-display font-semibold text-base">{p.name}</div>
                </div>
                <p className="text-sm text-neutral-600 leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CorporateContactBlock brand={b} />
    </PublicSiteShell>
  );
}

/* ================================================================== */
/*  CORPORATE — PILLAR DETAIL ROUTER                                   */
/* ================================================================== */
export function CorporatePillarRouter() {
  const { slug } = useParams();
  const pillar = CORPORATE_PILLARS.find((p) => p.slug === slug);
  if (!pillar) return <Navigate to="/corporate" replace />;
  return <CorporatePillarDetail pillar={pillar} />;
}

function CorporatePillarDetail({ pillar }) {
  const b = CORPORATE_BRAND;
  const Icon = ICON_BY_PILLAR[pillar.slug] || Briefcase;
  const related = CORPORATE_PILLARS.filter((p) => p.slug !== pillar.slug);

  return (
    <PublicSiteShell brand={b} menu={CORPORATE_MENU} chatContext={`Corporate Hub visitor — viewing ${pillar.title}.`}>
      <section className="relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${b.primary} 0%, #2a2620 100%)`, color: '#FFFFFF' }} data-testid={`pillar-hero-${pillar.slug}`}>
        <div className="absolute -top-32 -right-32 w-[400px] h-[400px] rounded-full blur-[120px]" style={{ background: `${b.accent}25` }} />
        <div className="container-x relative pt-16 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.22em] font-semibold mb-4 inline-flex items-center gap-2" style={{ color: b.accent }}>
              <Link to="/corporate" className="hover:underline">Corporate Hub</Link>
              <ChevronRight className="w-3.5 h-3.5" />
              <span>Service Pillar</span>
            </div>
            <div className="inline-flex items-center gap-3 mb-5">
              <div className="h-14 w-14 rounded-md flex items-center justify-center" style={{ background: `${b.accent}20`, color: b.accent }}>
                <Icon className="w-7 h-7" strokeWidth={1.5} />
              </div>
            </div>
            <h1 className="font-display font-bold tracking-tight text-4xl lg:text-5xl leading-[1.05]">
              {pillar.title}
            </h1>
            <p className="mt-5 text-lg leading-relaxed max-w-2xl" style={{ color: 'rgba(255,255,255,0.78)' }}>{pillar.intro}</p>
          </div>
          <div className="lg:col-span-5">
            <div className="aspect-[4/3] rounded-md overflow-hidden border shadow-2xl" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
              <img src={pillar.hero} alt={pillar.title} className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-20 bg-white">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.22em] font-semibold mb-3" style={{ color: b.accent }}>Practice overview</div>
            <h2 className="font-display font-bold text-2xl lg:text-3xl mb-5 leading-tight">{pillar.short}</h2>
            <p className="text-base text-neutral-700 leading-relaxed">{pillar.body}</p>

            <div className="mt-10">
              <div className="text-xs uppercase tracking-[0.22em] font-semibold mb-4" style={{ color: b.accent }}>Sub-sections & focus areas</div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {pillar.items.map((item) => (
                  <li key={item} className="flex items-start gap-2.5 p-3 rounded-md border" style={{ borderColor: '#EEEEEE' }}>
                    <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: b.accent }} />
                    <span className="text-sm text-neutral-700 leading-snug">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-10 p-6 rounded-md" style={{ background: '#FAF8F2' }}>
              <p className="text-base text-neutral-700 leading-relaxed italic">&ldquo;{pillar.closing}&rdquo;</p>
            </div>
          </div>

          <aside className="lg:col-span-5 lg:sticky lg:top-28 self-start space-y-4">
            <div className="p-6 rounded-md text-white" style={{ background: b.primary }}>
              <div className="text-xs uppercase tracking-[0.22em] font-semibold mb-3" style={{ color: b.accent }}>Engage this practice</div>
              <div className="font-display font-bold text-xl mb-4">Talk to our consulting team</div>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start gap-2.5"><Mail className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: b.accent }} /><div><div className="text-white/95">{b.email}</div><div className="text-[11px]" style={{ color: 'rgba(255,255,255,0.55)' }}>Corporate Hub enquiries</div></div></li>
                <li className="flex items-start gap-2.5"><Phone className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: b.accent }} /><div><div className="text-white/95">{b.phone}</div><div className="text-[11px]" style={{ color: 'rgba(255,255,255,0.55)' }}>Lead Consultant direct line</div></div></li>
                <li className="flex items-start gap-2.5"><MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: b.accent }} /><div><div className="text-white/95">{b.address}</div></div></li>
              </ul>
              <Link to="/about/contact-us" className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 rounded-md font-semibold text-sm" style={{ background: b.accent, color: b.primary }} data-testid={`pillar-cta-${pillar.slug}`}>
                Request a Consultation <ChevronRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="p-6 rounded-md border" style={{ borderColor: '#E5E7EB' }}>
              <div className="text-xs uppercase tracking-[0.22em] font-semibold mb-3" style={{ color: b.accent }}>Engagement signals</div>
              <ul className="space-y-2 text-sm text-neutral-700">
                <li className="flex items-start gap-2"><Globe2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: b.accent }} />Pan-African delivery across 13+ countries</li>
                <li className="flex items-start gap-2"><Award className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: b.accent }} />Certified, credentialled programmes and consultants</li>
                <li className="flex items-start gap-2"><Sparkles className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: b.accent }} />Customised engagements — no off-the-shelf delivery</li>
                <li className="flex items-start gap-2"><Building className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: b.accent }} />Sustained accompaniment until systems are embedded</li>
              </ul>
            </div>
          </aside>
        </div>
      </section>

      <section className="py-16 lg:py-20" style={{ background: '#FAF8F2' }}>
        <div className="container-x">
          <div className="text-xs uppercase tracking-[0.22em] font-semibold mb-3" style={{ color: b.accent }}>Related pillars</div>
          <h3 className="font-display font-bold text-2xl lg:text-3xl mb-8 leading-tight">Explore the rest of our practice</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {related.map((p) => {
              const RIcon = ICON_BY_PILLAR[p.slug] || Briefcase;
              return (
                <Link key={p.slug} to={`/corporate/${p.slug}`} className="group p-6 bg-white rounded-md border hover:-translate-y-1 hover:shadow-xl transition-all" style={{ borderColor: '#E5E7EB' }}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="h-12 w-12 rounded-md flex items-center justify-center" style={{ background: `${b.accent}15`, color: b.accent }}>
                      <RIcon className="w-6 h-6" strokeWidth={1.5} />
                    </div>
                    <ArrowRight className="w-4 h-4 text-neutral-300 group-hover:translate-x-1 transition-all" />
                  </div>
                  <h4 className="font-display font-semibold text-lg mb-1.5">{p.title}</h4>
                  <p className="text-sm text-neutral-600 leading-relaxed">{p.short}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <CorporateContactBlock brand={b} />
    </PublicSiteShell>
  );
}

function CorporateContactBlock({ brand: b }) {
  return (
    <section id="contact" className="py-20" style={{ background: b.primary, color: '#FFFFFF' }}>
      <div className="container-x text-center">
        <div className="text-xs uppercase tracking-[0.22em] font-semibold mb-3" style={{ color: b.accent }}>Engage our practice</div>
        <h2 className="font-display font-bold text-3xl lg:text-4xl mb-4 leading-tight">
          Ready to design the next chapter of your institution?
        </h2>
        <p className="max-w-2xl mx-auto text-base mb-8" style={{ color: 'rgba(255,255,255,0.78)' }}>
          We welcome confidential conversations with senior leaders and boards. Reach out — we will return your enquiry within 24 hours with a structured next step.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <Link to="/about/contact-us" className="px-6 py-3 rounded-md font-semibold text-sm inline-flex items-center gap-2" style={{ background: b.accent, color: b.primary }} data-testid="corporate-contact-cta">
            Request a Consultation <ChevronRight className="w-4 h-4" />
          </Link>
          <a href={`mailto:${b.email}`} className="px-6 py-3 rounded-md font-semibold text-sm border border-white/25 text-white hover:bg-white/10 transition-colors inline-flex items-center gap-2">
            <Mail className="w-4 h-4" /> {b.email}
          </a>
        </div>
      </div>
    </section>
  );
}
